'use client';
import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, ShieldAlert, Zap, Truck, Globe, Activity } from 'lucide-react';
import Link from 'next/link';
import { getRiskEvents } from '@/lib/storage';
import { DEFAULT_RISK_EVENTS, RiskEvent } from '@/lib/riskData';
import Icon from '@/components/ui/AppIcon';


function Sparkline({ data }: { data: number[] }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 80;
  const height = 28;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke="var(--danger)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function computeKPIs(events: RiskEvent[]) {
  const critical = events.filter(e => e.severity === 'Critical').length;
  const high = events.filter(e => e.severity === 'High').length;
  const medium = events.filter(e => e.severity === 'Medium').length;
  const escalating = events.filter(e => e.status === 'Escalating').length;
  const sanctionsEvents = events.filter(e => e.category === 'Sanctions');
  const cyberEvents = events.filter(e => e.category === 'Cyber');
  const maritimeEvents = events.filter(e => e.category === 'Maritime');
  const supplyEvents = events.filter(e => e.category === 'Supply Chain');
  const instabilityEvents = events.filter(e => e.category === 'Regional Instability');

  // Composite threat index: weighted by category severity
  const sanctionsScore = Math.min(sanctionsEvents.reduce((s, e) => s + (e.severity === 'Critical' ? 25 : e.severity === 'High' ? 15 : 8), 0), 20);
  const cyberScore = Math.min(cyberEvents.reduce((s, e) => s + (e.severity === 'Critical' ? 25 : e.severity === 'High' ? 15 : 8), 0), 20);
  const politicalScore = Math.min(instabilityEvents.reduce((s, e) => s + (e.severity === 'Critical' ? 25 : e.severity === 'High' ? 15 : 8), 0), 20);
  const maritimeScore = Math.min(maritimeEvents.reduce((s, e) => s + (e.severity === 'Critical' ? 25 : e.severity === 'High' ? 15 : 8), 0), 20);
  const supplyScore = Math.min(supplyEvents.reduce((s, e) => s + (e.severity === 'Critical' ? 25 : e.severity === 'High' ? 15 : 8), 0), 20);
  const threatIndex = Math.round(sanctionsScore + cyberScore + politicalScore + maritimeScore + supplyScore);

  const cyberThreatLevel = Math.min(Math.round(cyberEvents.reduce((s, e) => s + (e.severity === 'Critical' ? 30 : e.severity === 'High' ? 20 : 10), 0) + cyberEvents.length * 5), 100);
  const operationalExposure = Math.min(Math.round((critical * 12 + high * 8 + escalating * 6 + sanctionsEvents.length * 10) / Math.max(events.length, 1) * 10), 100);

  return {
    threatIndex,
    totalEvents: events.length,
    critical,
    high,
    medium,
    escalating,
    sanctionsCount: sanctionsEvents.length,
    cyberThreatLevel,
    maritimeCount: maritimeEvents.length + supplyEvents.length,
    operationalExposure,
    cyberCampaigns: cyberEvents.length,
  };
}

export default function KPIBentoGrid() {
  const [kpis, setKpis] = useState(() => computeKPIs(DEFAULT_RISK_EVENTS));

  useEffect(() => {
    const load = () => {
      const stored = getRiskEvents();
      const events = stored.length > 0 ? stored : DEFAULT_RISK_EVENTS;
      setKpis(computeKPIs(events));
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const kpiData = [
    {
      id: 'kpi-threat-index',
      label: 'Global Threat Index',
      value: kpis.threatIndex.toString(),
      unit: '/ 100',
      change: kpis.threatIndex > 70 ? '+3.1' : '+1.2',
      changeDir: 'up' as const,
      changeSemantic: 'bad' as const,
      subtext: kpis.threatIndex > 70 ? 'Elevated — multi-domain threat' : 'Moderate threat environment',
      icon: Activity,
      hero: true,
      alertState: kpis.threatIndex > 65,
      sparkline: [52, 48, 61, 57, 65, 71, 68, kpis.threatIndex],
      href: '/ai-risk-dashboard',
      methodology: 'AI-weighted composite: Sanctions 20% · Cyber 20% · Political 20% · Maritime 20% · Supply Chain 20%',
    },
    {
      id: 'kpi-escalations',
      label: 'Active Escalations',
      value: kpis.totalEvents.toString(),
      unit: 'events',
      change: `+${kpis.critical + kpis.escalating} critical`,
      changeDir: 'up' as const,
      changeSemantic: 'bad' as const,
      subtext: `${kpis.critical} Critical, ${kpis.high} High, ${kpis.medium} Medium`,
      icon: AlertTriangle,
      hero: false,
      alertState: kpis.critical > 0,
      href: '/alerts',
    },
    {
      id: 'kpi-sanctions',
      label: 'Sanctions Events',
      value: kpis.sanctionsCount.toString(),
      unit: 'active',
      change: '+2 this week',
      changeDir: 'up' as const,
      changeSemantic: 'warning' as const,
      subtext: '2 touch Acme Corp suppliers',
      icon: ShieldAlert,
      hero: false,
      alertState: kpis.sanctionsCount > 1,
      href: '/sanctions-tracker',
    },
    {
      id: 'kpi-cyber',
      label: 'Cyber Threat Level',
      value: kpis.cyberThreatLevel.toString(),
      unit: '/ 100',
      change: '-4.2',
      changeDir: 'down' as const,
      changeSemantic: 'good' as const,
      subtext: `${kpis.cyberCampaigns} APT campaigns active`,
      icon: Zap,
      hero: false,
      alertState: kpis.cyberThreatLevel > 60,
      href: '/cyber-risk',
    },
    {
      id: 'kpi-supply',
      label: 'Supply Chain Disruptions',
      value: kpis.maritimeCount.toString(),
      unit: 'routes',
      change: '+7 vs last week',
      changeDir: 'up' as const,
      changeSemantic: 'bad' as const,
      subtext: 'Red Sea, Taiwan Strait affected',
      icon: Truck,
      hero: false,
      alertState: kpis.maritimeCount > 2,
      href: '/supply-chain',
    },
    {
      id: 'kpi-exposure',
      label: 'Operational Exposure',
      value: kpis.operationalExposure.toString(),
      unit: '/ 100',
      change: '+1.4',
      changeDir: 'up' as const,
      changeSemantic: 'warning' as const,
      subtext: 'Acme Corp — 3 active exposures',
      icon: Globe,
      hero: false,
      alertState: false,
      href: '/company-analyser',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {kpiData.map((kpi) => {
        const Icon = kpi.icon;
        const isGood = kpi.changeSemantic === 'good';
        const isBad = kpi.changeSemantic === 'bad';
        const changeColor = isGood ? 'text-success' : isBad ? 'text-danger' : 'text-warning';
        const TrendIcon = kpi.changeDir === 'up' ? TrendingUp : TrendingDown;

        if (kpi.hero) {
          return (
            <Link
              key={kpi.id}
              href={kpi.href || '#'}
              className="col-span-2 card-elevated card-glow-danger p-5 rounded-xl border border-danger/20 bg-danger/5 relative overflow-hidden hover:border-danger/40 transition-all duration-200 block"
            >
              <div className="absolute top-0 right-0 w-32 h-32 blob-accent pointer-events-none opacity-30" />
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-danger/20 flex items-center justify-center">
                    <Icon size={16} className="text-danger" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{kpi.label}</p>
                  </div>
                </div>
                <span className="text-2xs px-2 py-0.5 bg-danger/20 text-danger rounded-md font-semibold uppercase tracking-wide">
                  {kpi.alertState ? 'ELEVATED' : 'MODERATE'}
                </span>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-bold text-foreground font-tabular">{kpi.value}</span>
                    <span className="text-sm text-muted-foreground">{kpi.unit}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendIcon size={12} className={changeColor} />
                    <span className={`text-xs font-semibold ${changeColor}`}>{kpi.change}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{kpi.subtext}</p>
                  {kpi.methodology && (
                    <p className="text-2xs text-muted-foreground/70 leading-relaxed max-w-xs">{kpi.methodology}</p>
                  )}
                </div>
                {kpi.sparkline && (
                  <div className="opacity-60">
                    <Sparkline data={kpi.sparkline} />
                  </div>
                )}
              </div>
            </Link>
          );
        }

        return (
          <Link
            key={kpi.id}
            href={kpi.href || '#'}
            className={`card-elevated p-4 rounded-xl border transition-all duration-200 hover:border-primary/20 block ${
              kpi.alertState ? 'border-warning/20 bg-warning/5' : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                kpi.alertState ? 'bg-warning/20' : 'bg-muted'
              }`}>
                <Icon size={14} className={kpi.alertState ? 'text-warning' : 'text-muted-foreground'} />
              </div>
              <div className="flex items-center gap-1">
                <TrendIcon size={11} className={changeColor} />
                <span className={`text-2xs font-semibold ${changeColor}`}>{kpi.change}</span>
              </div>
            </div>
            <div className="flex items-baseline gap-1 mb-0.5">
              <span className="text-2xl font-bold text-foreground font-tabular">{kpi.value}</span>
              <span className="text-xs text-muted-foreground">{kpi.unit}</span>
            </div>
            <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{kpi.label}</p>
            <p className="text-2xs text-muted-foreground truncate">{kpi.subtext}</p>
          </Link>
        );
      })}
    </div>
  );
}